import React, { FC, ChangeEvent, SyntheticEvent, useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import { DeprecatedControlledSelect } from 'transfix-form/components/ControlledSelect'
import { DeprecatedControlledTextInput } from 'transfix-form/components/ControlledTextInput'
import { Button } from 'transfix-ui/components/Button'
import { ButtonGroup } from 'transfix-ui/components/ButtonGroup'
import { Checkbox } from 'transfix-ui/components/Checkbox'
import { FormModal } from 'transfix-ui/components/Modals'
import Box from 'transfix-ui/core/Box'
import makeStyles from 'transfix-ui/core/styles/makeStyles'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { v4 as uuid } from 'uuid'
import { GenerateNormFileResponse, useGenerateNormFile } from 'apps/marketplace/apiTypes/instantRfp'
import { CsvPreview } from 'apps/marketplace/components/CsvPreview'
import ShipperAutosuggest, { IShipperResult } from 'apps/marketplace/components/ShipperAutosuggest'
import {
  NormFileSchema,
  INormFileSchema,
  NormFileInputSchema,
} from 'apps/marketplace/pages/instant-rfp/schema/NormFileSchema'
import { IFile, SHIPPER_TYPE, RFP_TYPE } from 'apps/marketplace/pages/instant-rfp/types'
import { validateCsv, ICsvErrorsResult } from 'apps/marketplace/utils/helpers/csv'
import { AttachmentIcon, CheckFilledIcon } from 'transfix-icons'
import { filePromises } from 'utils/fileUploadHelper'
import { getUserCookieValues } from 'utils/userAuth'

const { userEmail } = getUserCookieValues()

const normModalStyles = makeStyles(theme => ({
  checkbox: {
    marginLeft: theme.spacing(0.25),
  },
}))

export const requestForProposalTypes = [
  {
    value: RFP_TYPE.rfp,
    label: 'RFP',
  },
  {
    value: RFP_TYPE.reprice,
    label: 'Reprice',
  },
  {
    value: RFP_TYPE.dataAnalysis,
    label: 'Data Analysis',
  },
  {
    value: RFP_TYPE.shipperRateIndex,
    label: 'Shipper Rate Index',
  },
]

interface INormFileModalProps {
  onSuccess: (toastMessage: string) => void
  onError: (toastMessage: string) => void
  onClose: () => void
}

export const NormFileModal: FC<INormFileModalProps> = ({
  onClose,
  onError,
  onSuccess,
}: INormFileModalProps) => {
  const pricingInStorm = useFeatureFlag('costInStarkDashboardStrategyInformation')
  const [showCsvPreview, setShowCsvPreview] = useState<boolean>(false)
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvErrors, setCsvErrors] = useState<ICsvErrorsResult | null>(null)
  const { loading, mutate: submitNormFile } = useGenerateNormFile({})
  const methods = useForm<INormFileSchema>({
    resolver: yupResolver(NormFileSchema),
    defaultValues: {
      normFileTemplate: {
        file: '',
        fileFileName: '',
        uuid: '',
      },
      shipperApplication: undefined,
      newShipperName: '',
      googleSheetsUrl: '',
      salesforceRfpId: '',
      email: userEmail,
      storeInDatabase: true,
      generateMatchingCarriers: true,
      auditWorkspace: false,
      pricingWorkspace: false,
      shipperType: SHIPPER_TYPE.existing,
      requestForProposalType: RFP_TYPE.rfp,
    },
  })
  const { control, errors, handleSubmit, setValue, watch } = methods
  const generateNormFile = (formParams: INormFileSchema) => {
    const {
      auditWorkspace,
      email,
      generateMatchingCarriers,
      googleSheetsUrl,
      newShipperName,
      normFileTemplate,
      requestForProposalType,
      salesforceRfpId,
      shipperApplication,
      shipperType,
      storeInDatabase,
    } = formParams
    submitNormFile({
      rfp: {
        email,
        googleSheetsUrl,
        normFileTemplate,
        salesforceRfpId,
        shipperApplicationId:
          shipperType === SHIPPER_TYPE.existing ? shipperApplication?.id : undefined,
        newShipperName: shipperType === SHIPPER_TYPE.new ? newShipperName : undefined,
        requestForProposalType,
        storeInDatabase,
        generateMatchingCarriers,
        auditWorkspace,
        pricingWorkspace: pricingInStorm && auditWorkspace,
      },
    })
      .then((response: GenerateNormFileResponse) => {
        onSuccess(response.message)
        onClose()
      })
      .catch(err => {
        onError(err.data?.errors || 'An unknown error occurred.')
      })
  }

  const {
    auditWorkspace,
    email,
    generateMatchingCarriers,
    googleSheetsUrl,
    newShipperName,
    normFileTemplate,
    salesforceRfpId,
    shipperApplication,
    shipperType,
  } = watch([
    'normFileTemplate',
    'shipperType',
    'googleSheetsUrl',
    'email',
    'shipperApplication',
    'newShipperName',
    'salesforceRfpId',
    'generateMatchingCarriers',
    'auditWorkspace',
  ])

  const requiredFieldsFilledOut =
    !!normFileTemplate?.file &&
    !!googleSheetsUrl &&
    !!email &&
    !!(
      (shipperType === SHIPPER_TYPE.new && !!newShipperName) ||
      (shipperType === SHIPPER_TYPE.existing && !!shipperApplication)
    ) &&
    !!salesforceRfpId

  const runCsvValidation = async () => {
    if (csvFile) {
      const result = await validateCsv(csvFile, NormFileInputSchema)
      setCsvErrors(result)
    }
  }

  const shouldShowCsvPreview = csvFile && (showCsvPreview || (csvErrors && csvErrors.count > 0))

  useEffect(() => {
    runCsvValidation()
  }, [csvFile])

  return (
    <FormModal
      open
      title='Generate Norm File'
      size={5}
      onClose={onClose}
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
          dataTest: 'generate-norm-file-dismiss-button',
          action: onClose,
        },
        {
          label: 'Generate Norm File',
          type: 'confirm',
          dataTest: 'generate-norm-file-submit-button',
          action: handleSubmit(generateNormFile),
          disabled: !requiredFieldsFilledOut || loading,
        },
      ]}>
      <Box mb={2}>
        <Controller
          name='normFileTemplate'
          control={control}
          render={({ onChange, value }) => {
            const fileUploadHandler = (e: ChangeEvent<HTMLInputElement>) => {
              const {
                target: { files },
              } = e
              if (files) {
                const fileList = Array.from(files)
                setCsvFile(files[0])
                Promise.all(filePromises(fileList)).then(packageFiles => {
                  const pkgFiles = packageFiles.map(pkg => {
                    const pkgFile = pkg as IFile
                    pkgFile.uuid = uuid()
                    return pkgFile
                  })
                  onChange(pkgFiles[0])
                })
              }
            }
            const resetHandler = () => {
              onChange({
                file: '',
                fileFileName: '',
                uuid: '',
              })
              setCsvFile(null)
              setCsvErrors(null)
            }
            return (
              <>
                <Box display='flex' alignItems='center' mb={2}>
                  {!value?.fileFileName && (
                    <Button
                      color='tertiary'
                      className='btn-fileinput btn-icon btn-icon-tertiary flex vertically-center'
                      id='file-upload'
                      data-test='upload-files'>
                      <AttachmentIcon className='inline-block' size='large' />
                      <span className='btn-icon-label'>Attach Template</span>
                      <input
                        type='file'
                        id='uploads'
                        name='uploads'
                        accept='.csv'
                        onChange={fileUploadHandler}
                      />
                    </Button>
                  )}
                  {value?.fileFileName && (
                    <>
                      <CheckFilledIcon color='info' size='large' />
                      <div className='ml2'>{value.fileFileName}</div>
                      <Button color='tertiary' className='ml2' onClick={resetHandler}>
                        Delete
                      </Button>
                      <Button
                        color='tertiary'
                        className='ml2'
                        onClick={() => setShowCsvPreview(!showCsvPreview)}>
                        {shouldShowCsvPreview ? 'Hide Preview' : 'Show Preview'}
                      </Button>
                    </>
                  )}
                  {errors.normFileTemplate && (
                    <span className='validation-message text-error'>
                      {errors.normFileTemplate?.fileFileName?.message}
                    </span>
                  )}
                </Box>
                {shouldShowCsvPreview && (
                  <Box mb={2}>
                    <CsvPreview
                      file={csvFile}
                      errors={csvErrors?.errors}
                      maxRows={5}
                      maxColumns={5}
                    />
                  </Box>
                )}
              </>
            )
          }}
        />
        <DeprecatedControlledTextInput
          control={control}
          name='salesforceRfpId'
          TextInputProps={{
            name: 'salesforceRfpId',
            label: 'Salesforce RFP ID',
            placeholder: 'Enter Salesforce ID',
            invalid: !!errors.salesforceRfpId,
            validationMessage: errors.salesforceRfpId?.message,
            className: 'mb3',
            dataTest: 'salesforce-rfp-id',
            required: true,
          }}
        />
        <DeprecatedControlledTextInput
          control={control}
          name='email'
          TextInputProps={{
            name: 'email',
            type: 'email',
            label: 'Email',
            placeholder: 'Enter email',
            invalid: !!errors.email,
            validationMessage: errors.email?.message,
            className: 'mb3',
            dataTest: 'email',
            required: true,
          }}
        />
        <Box display='flex' justifyContent='space-between' alignItems='flex-end' mb={2}>
          <Box width='50%' mr={1}>
            <Controller
              name='shipperType'
              control={control}
              render={({ onChange, value }) => {
                const shipperTypeHandler = (selection: string) => {
                  onChange(selection as SHIPPER_TYPE)
                }
                return (
                  <ButtonGroup
                    className='dialog-medium w-100'
                    value={value}
                    onChange={shipperTypeHandler}
                    hideLabel>
                    <Button value={SHIPPER_TYPE.existing}>Existing Shipper</Button>
                    <Button value={SHIPPER_TYPE.new}>New Shipper</Button>
                  </ButtonGroup>
                )
              }}
            />
          </Box>
          <Box width='50%' ml={1}>
            {shipperType === SHIPPER_TYPE.existing ? (
              <Controller
                name='shipperApplication'
                control={control}
                render={({ onChange, value }) => {
                  const existingShipperHandler = (result: Partial<IShipperResult> | undefined) => {
                    onChange({
                      name: result?.name,
                      id: result?.id,
                    })
                  }
                  return (
                    <ShipperAutosuggest
                      label='Existing Shipper Name'
                      onChange={existingShipperHandler}
                      value={value?.name}
                      dataTest='existing-shipper-name'
                      confirmed={false}
                      required
                      large
                    />
                  )
                }}
              />
            ) : (
              <DeprecatedControlledTextInput
                control={control}
                name='newShipperName'
                TextInputProps={{
                  name: 'newShipperName',
                  label: 'New Shipper Name',
                  placeholder: 'Enter new shipper name',
                  invalid: !!errors.newShipperName,
                  validationMessage: errors.newShipperName?.message,
                  dataTest: 'new-shipper-name',
                  required: true,
                  large: true,
                }}
              />
            )}
          </Box>
        </Box>
        <DeprecatedControlledTextInput
          control={control}
          name='googleSheetsUrl'
          TextInputProps={{
            name: 'googleSheetsUrl',
            label: 'Google Sheets URL',
            placeholder: 'Enter URL',
            invalid: !!errors.googleSheetsUrl,
            validationMessage: errors.googleSheetsUrl?.message,
            className: 'mb3',
            dataTest: 'google-sheets-url',
            required: true,
          }}
        />
        <DeprecatedControlledSelect
          control={control}
          name='requestForProposalType'
          SelectProps={{
            label: 'RFP Type',
            options: requestForProposalTypes,
            className: 'mb3',
            dataTest: 'rfp-type',
            required: true,
            large: true,
          }}
        />
        <Controller
          name='generateMatchingCarriers'
          control={control}
          render={({ onChange, value }) => {
            const onChangeHandler = (val: SyntheticEvent<HTMLInputElement>) => {
              onChange(val.currentTarget.checked)
              // When "generateMatchingCarriers" is checked,
              // "storeInDatabase" should be checked automatically:
              if (val.currentTarget.checked) {
                setValue('storeInDatabase', true)
              }
            }
            return (
              <Box className={normModalStyles().checkbox}>
                <Checkbox
                  label='Look for Carrier Matches'
                  checked={value}
                  onChange={onChangeHandler}
                  dataTest='carrier-matches'
                />
              </Box>
            )
          }}
        />
        <Controller
          name='auditWorkspace'
          control={control}
          render={({ onChange, value }) => {
            const onChangeHandler = (val: SyntheticEvent<HTMLInputElement>) => {
              onChange(val.currentTarget.checked)
              if (val.currentTarget.checked) {
                setValue('storeInDatabase', true)
              }
            }
            return (
              <Box className={normModalStyles().checkbox}>
                <Checkbox
                  checked={value}
                  onChange={onChangeHandler}
                  label='Use Contract Pricing Workspace'
                  dataTest='use-caw'
                />
              </Box>
            )
          }}
        />
        <Controller
          name='storeInDatabase'
          control={control}
          render={({ onChange, value }) => {
            const onChangeHandler = (val: SyntheticEvent<HTMLInputElement>) => {
              onChange(val.currentTarget.checked)
            }
            return (
              <Box className={normModalStyles().checkbox}>
                <Checkbox
                  label='Store in Database'
                  checked={value}
                  onChange={onChangeHandler}
                  disabled={generateMatchingCarriers || auditWorkspace}
                  dataTest='store-in-database'
                />
              </Box>
            )
          }}
        />
      </Box>
    </FormModal>
  )
}
